import * as React from 'react'
// components
import Grid from '@mui/material/Grid2'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// view components
import CreateReviewForm from 'components/Modals/CreateReviewModal/components/ReviewDetails/components/Form'
import ActivitiesListContainer from 'containers/Activities/components/ActivitiesList'
// view
import {
  ApproveButtonWrapper,
  BackIcon,
  BackIconWrapper,
  ContentWrapper,
  EditIcon,
  EditIconWrapper,
  Scrollbar,
  TitleWrapper,
  TitleMeta,
  Wrapper
} from './views'
// context
import { Forms, Modals } from 'constants/index'
// graphql
import { REVIEW_ACTIVITIES_LIST_QUERY } from 'graphql/reviews/queries'
// hooks
import { useCurrentUser, useShowModal } from 'hooks/index'
import { useApproveReview, useReviewQuestions } from 'hooks/reviews'
// types
import { LogEntityTypes } from 'types/graphql'
// utils
import moment from 'moment'
import { get } from 'lodash'
import { generateUserName } from 'utils/user'
import { isReviewApprover, isUserReviewer, mapReviewPicklistsToFormValues } from 'utils/reviews'

interface IIncomingProps {
  entity: any
  review?: any
  hideModal: () => void
}

const ReviewResult: React.FunctionComponent<IIncomingProps> = ({ entity, hideModal, review }) => {
  const { showModal } = useShowModal({ modalType: Modals.REVIEW_CREATE_MODAL })

  const currentUser = useCurrentUser()

  const { questions, picklists } = useReviewQuestions(entity?.entityType)
  const { approveReview, isApproving } = useApproveReview()

  // methods
  const handleShowCreateReviewModal = () => {
    showModal({
      isUpdate: true,
      review,
      entity
    })
    hideModal()
  }

  const handleApproveReview = async () => {
    const isApproved = await approveReview(review, entity)

    if (isApproved) {
      hideModal()
    }
  }

  // constants
  const initialValues = {
    goals: get(review, 'values.goals'),
    doWell: get(review, 'values.doWell'),
    doBetter: get(review, 'values.doBetter'),
    picklists: mapReviewPicklistsToFormValues(picklists, review)
  }

  const shouldShowEditButton = isUserReviewer(review, currentUser)
  const shouldShowApproveButton = isReviewApprover(review, currentUser)

  return (
    <Wrapper>
      <Grid container justifyContent={'space-between'}>
        <BackIconWrapper onClick={hideModal}>
          <BackIcon />
        </BackIconWrapper>
        {shouldShowEditButton && (
          <EditIconWrapper
            item
            md={2}
            container
            alignItems={'center'}
            justifyContent={'flex-end'}
            onClick={handleShowCreateReviewModal}>
            <EditIcon />
          </EditIconWrapper>
        )}
      </Grid>

      <Scrollbar>
        <TitleWrapper>
          <span>Review </span>
          <span>{`${review.startPeriod.format('D MMM Y')} to ${review.endPeriod.format('D MMM Y')}`}</span>
          <TitleMeta>{`Updated by ${generateUserName(review.updatedBy)} on ${moment(review.updatedAt).format(
            'D MMM Y'
          )}`}</TitleMeta>
          {review.approvedAt && review.approvedBy && (
            <TitleMeta>{`Approved by ${generateUserName(review.approvedBy)} on ${moment(review.approvedAt).format(
              'D MMM Y'
            )}`}</TitleMeta>
          )}
        </TitleWrapper>

        <ContentWrapper container direction={'column'}>
          <CreateReviewForm
            isReadOnly
            questions={questions}
            picklists={picklists}
            form={Forms.REVIEW_DETAILS_FORM}
            initialValues={initialValues}
          />
          <ActivitiesListContainer
            isReviewDetails
            isReadOnly
            disableDrag
            entity={{ id: review.id, entityType: LogEntityTypes.Review }}
            query={REVIEW_ACTIVITIES_LIST_QUERY}
            variables={{
              filter: { entityId: review.id }
            }}
          />
        </ContentWrapper>
      </Scrollbar>
      {shouldShowApproveButton && (
        <ApproveButtonWrapper>
          <Button type={'button'} isLoading={isApproving} layout={BUTTON_LAYOUTS.PURPLE} onClick={handleApproveReview}>
            Approve Review
          </Button>
        </ApproveButtonWrapper>
      )}
    </Wrapper>
  )
}

export default ReviewResult
